import * as React from "react";
import { History } from "history";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { selectSingleLineMode } from "./reducers/selectors";
import Toggle from "react-toggle";
import { classNames } from "../../../lib/Util";

import * as BusinessLogic from "../../../lib/BusinessLogic";
import GanttChart from "./GanttChart/GanttChart";
import Controls from "./Controls";
import ZoomToolbar from "./ZoomToolbar";
import LineStatusesPoller from "./LineStatusesPoller";
import PlanningDetails from "./PlanningDetails/PlanningDetails";
import ResizableHandle from "./ResizableHandle";
import ShowGanttOrderDataOnClick from "./EventListeners/ShowGanttOrderDataOnClick";
import OrderAdvancedMenu from "./OrderAdvancedMenu";
import InsightTypesSelect from "./InsightTypesSelect";
import DisplayTypesMenu from "./DisplayTypesMenu";
import GanttPanes from "./GanttPanes";
import Loader from "../../Loader";
import { INSIGHT_TYPES, LINE_GROUP_TAGS_ACCESS, PLANT_TAGS_ACCESS } from "../../../lib/ManufacturingTags.generated";
import { INSIGHT_ACTIONS } from "../../../lib/Models";
import { clamp } from "../../../lib/Util";
import {
    GanttChartSource,
    RESET_PROPERTIES,
    UPDATE_PROPERTIES_ACTION_NAME,
    TOGGLE_WATERFALL_MODE,
    UPDATE_PLANNING_TABLE_HEIGHT,
    UPDATE_PLANNING_TABLE_WIDTH,
    default_configuration,
    countRowsOnGantt,
} from "./reducers/properties";
import local_settings, {
    FIELD_PLANNING_DETAILS_WIDTH_RATIO,
    FIELD_PLANNING_DETAILS_HEIGHT_RATIO,
} from "../../../lib/LocalSettings";
import { RESET_GANTT } from "./reducers/common";
import { LinesOrdersLogic, ReportCache, ReportLogic } from "./reducers/BusinessLogic";
import { PLANNING_TABLE_DISPLAY_TYPES } from "./DisplayTypesMenu";
import * as mt from "../../../lib/backend/manufacturing2.generated.types";
import KpisContainer from "./KpisContainer";
import InsightsList from "../InsightsList";
import { exportToolTransfer } from "../ToolTransferPrint";
import StockRequirementsGanttChart from "./GanttChart/StockRequirementsGanttChart";
import PlanningTableEditModal, { onSaveComment } from "./PlanningTableEditModal";
import { publishSync, MSG_TYPES } from "../../../lib/PubSub";

import type { IOrderModelBase } from "../../../lib/backend/manufacturing2.generated.types";
import type { UpdateLineOrderComment } from "./reducers/linesOrders";
import type {
    UpdatePropertiesActionType,
    ResetProperties,
    ToggleWaterfallModeAction,
    UpdatePlanningTableWidthAction,
    UpdatePlanningTableHeightAction,
} from "./reducers/properties";
import type { IReportModelEx } from "../../../lib/SimulationReportModels";
import type { ReportState, ResetReportAction } from "./reducers/report";
import type {
    LineOrders,
    ResetLinesOrdersAction,
    SetUnscheduledOrdersAction,
    UpdateFixedExtraLinesAction,
} from "./reducers/linesOrders";
import UnscheduledGanttChart from "./GanttChart/UnscheduledGanttChart";
import { translate } from "../../IntlProviderWrapper";
import MaterialStockForecastContainer from "./MaterialStockForecastContainer";
import { SNAP_TO_SHIFT } from "./Draggable/reducers";
import { HEADER_HEIGHT } from "../../ViewsCommon";

import type { LineOrdersLoadActions } from "./reducers/BusinessLogic";
import type { SetErrorAction } from "./reducers/planningTable";
import type { LinesOrdersParameters } from "./reducers/BusinessLogic";
import type { ResetValidLinesAction } from "./reducers/planningTable";
import type { ReduxState } from "./reducers/reducers";
import type { ResetGanttAction } from "./reducers/common";
import type { SnapToShiftAction } from "./Draggable/reducers";
import type { PlanningDetailsPosition } from "../../ViewsCommon";

export const VERSION_TYPE = {
    initial: "initial",
    current: "current",
    version: "version",
};

type ActionTypes =
    | ResetLinesOrdersAction
    | ResetReportAction
    | SetUnscheduledOrdersAction
    | ResetValidLinesAction
    | ResetGanttAction
    | UpdatePropertiesActionType
    | ResetProperties
    | UpdateLineOrderComment
    | SetErrorAction
    | LineOrdersLoadActions
    | UpdateFixedExtraLinesAction
    | ToggleWaterfallModeAction
    | UpdatePlanningTableWidthAction
    | UpdatePlanningTableHeightAction;

type OwnProps = {
    history: History;
    plant_uuid: string;
    line_group_uuid: string;
    show_planning_details_filter: boolean;
    planning_details_position: PlanningDetailsPosition;
    selected_display_type: keyof typeof PLANNING_TABLE_DISPLAY_TYPES;
    draggableDispatch: (args: SnapToShiftAction) => void;
    onDisplayTypeChanged: (display_type_value: keyof typeof PLANNING_TABLE_DISPLAY_TYPES) => void;
};

type Props = OwnProps & ReduxProps;

type State = {
    error: string;
    quick_report: IReportModelEx | null;
    table_width_ratio: number;
    table_height_ratio: number;
};

class PlanningTable extends React.Component<Props, State> {
    static DEFAULT_TABLE_WIDTH = 0.33;
    static DEFAULT_TABLE_HEIGHT = 0.5;

    state = {
        error: "",
        lines: [],
        quick_report: null,
        // If values fall outside typical bounds, revert to default to maintain a user-friendly UI
        table_width_ratio:
            local_settings.getNumberIfInRange(FIELD_PLANNING_DETAILS_WIDTH_RATIO, 0.05, 0.95) ??
            PlanningTable.DEFAULT_TABLE_WIDTH,
        table_height_ratio:
            local_settings.getNumberIfInRange(FIELD_PLANNING_DETAILS_HEIGHT_RATIO, 0.05, 0.95) ??
            PlanningTable.DEFAULT_TABLE_HEIGHT,
    };

    planningTableMainNode = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        ReportCache.removeCacheHash();
    }

    componentDidMount() {
        const config = default_configuration();
        const parameters: LinesOrdersParameters = {
            line_group_uuid: this.props.line_group_uuid,
            week: config.week,
            year: config.year,
            shift_edit_weeks: this.getShiftEditWeeks(this.props.line_group_uuid),
        };

        LinesOrdersLogic.load(parameters, this.props.reduxDispatch);

        this.props.reduxDispatch({
            type: UPDATE_PROPERTIES_ACTION_NAME,
            data: {
                line_group_uuid: this.props.line_group_uuid,
                source_type: GanttChartSource.planning_table_parallel,
            },
        });

        const plant_tags = BusinessLogic.getPlantTags(this.props.plant_uuid);
        this.props.draggableDispatch({
            type: SNAP_TO_SHIFT,
            data: PLANT_TAGS_ACCESS.planning_table_snap_to_shift(plant_tags),
        });
    }

    componentDidUpdate(prev_props: Props) {
        if (
            prev_props.show_planning_details_filter !== this.props.show_planning_details_filter ||
            prev_props.unscheduled_orders !== this.props.unscheduled_orders ||
            prev_props.show_stock_requirements !== this.props.show_stock_requirements
        ) {
            window.dispatchEvent(new Event("resize"));
        }

        if (prev_props.valid_lines !== this.props.valid_lines) {
            if (
                this.planningTableMainNode &&
                this.planningTableMainNode.current &&
                this.planningTableMainNode.current.scrollTop
            ) {
                this.planningTableMainNode.current.scrollTop = 0;
            }
        }

        const lg_or_plant_change =
            prev_props.line_group_uuid !== this.props.line_group_uuid ||
            prev_props.plant_uuid !== this.props.plant_uuid;
        if (lg_or_plant_change) {
            this.props.reduxDispatch({
                type: UPDATE_PROPERTIES_ACTION_NAME,
                data: {
                    source_type: GanttChartSource.planning_table_parallel,
                    line_group_uuid: this.props.line_group_uuid,
                },
            });
            this.props.reduxDispatch({ type: RESET_GANTT, data: undefined });
        }

        if (lg_or_plant_change && !this.props.lines_orders_loading) {
            ReportCache.removeCacheHash();

            const config = default_configuration();
            const parameters: LinesOrdersParameters = {
                line_group_uuid: this.props.line_group_uuid,
                week: config.week,
                year: config.year,
                shift_edit_weeks: this.getShiftEditWeeks(this.props.line_group_uuid),
            };

            LinesOrdersLogic.load(parameters, this.props.reduxDispatch);
        }
    }

    componentWillUnmount() {
        this.props.reduxDispatch({ type: RESET_GANTT, data: undefined });
        this.props.reduxDispatch({ type: RESET_PROPERTIES, data: undefined });
    }

    getShiftEditWeeks(line_group_uuid: string): number {
        // we need to load maximum of ERP Save weeks horizon and planning table weeks horizon
        const tags = BusinessLogic.getLineGroupTags(line_group_uuid);
        return Math.max(
            LINE_GROUP_TAGS_ACCESS.planning_table_save_to_erp_weeks(tags),
            LINE_GROUP_TAGS_ACCESS.planning_table_weeks(tags)
        );
    }

    handleResize = (movement: number) => {
        if (this.props.planning_details_position === "left") {
            const [min, max] = [0, window.innerWidth - ResizableHandle.WIDTH];
            const absolute_width = clamp(
                Math.round(this.state.table_width_ratio * window.innerWidth) + movement,
                min,
                max
            );
            this.setState({ table_width_ratio: absolute_width / window.innerWidth });
        } else {
            const parent_height = window.innerHeight - HEADER_HEIGHT;
            const [min, max] = [ResizableHandle.WIDTH, parent_height];
            const absolute_height = clamp(
                Math.round(this.state.table_height_ratio * parent_height) - movement,
                min,
                max
            );
            this.setState({ table_height_ratio: absolute_height / parent_height });
        }
    };

    handleResizeStop = () => {
        if (this.props.planning_details_position === "left") {
            local_settings.setItem(FIELD_PLANNING_DETAILS_WIDTH_RATIO, this.state.table_width_ratio.toString());
        } else {
            local_settings.setItem(FIELD_PLANNING_DETAILS_HEIGHT_RATIO, this.state.table_height_ratio.toString());
        }
    };

    getTableSizeRatio(): number {
        return this.props.planning_details_position === "left"
            ? this.state.table_width_ratio
            : this.state.table_height_ratio;
    }

    getResizableHandlePosition(): number {
        return this.props.planning_details_position === "left"
            ? this.state.table_width_ratio
            : 1 - this.state.table_height_ratio;
    }

    showWaterfallButton = (): boolean => {
        const tags = BusinessLogic.getLineGroupTags(this.props.line_group_uuid);

        return (
            LINE_GROUP_TAGS_ACCESS.planning_table_waterfall_chart(tags) &&
            this.props.single_line_mode &&
            this.props.planning_details_position === "left" &&
            !this.props.show_stock_requirements
        );
    };

    handleToggleWaterfallMode = () => {
        this.props.reduxDispatch({
            type: TOGGLE_WATERFALL_MODE,
            data: undefined,
        });
        this.props.reduxDispatch({
            type: UPDATE_PLANNING_TABLE_WIDTH,
            data: undefined,
        });

        const count_non_empty_lines = countRowsOnGantt({
            valid_lines_map: this.props.valid_lines_map,
            line_group_uuid: this.props.line_group_uuid,
            report_data: this.props.report.report_data,
            source_type: GanttChartSource.planning_table_parallel,
            count_orders: this.props.properties.waterfall_mode,
        });

        const num_unscheduled_lines = this.props.properties.num_unscheduled_lines;
        if (count_non_empty_lines) {
            this.props.reduxDispatch({
                type: UPDATE_PLANNING_TABLE_HEIGHT,
                data: {
                    count_non_empty_lines,
                    num_unscheduled_lines,
                },
            });
        }
        publishSync(MSG_TYPES.planning_table_align_scroll, { top: 0, left: 0, enforce: true });
        publishSync(MSG_TYPES.planning_table_align_scroll_end);
    };

    onDownloadHTML = () => {
        const event = new Event("export-html-insights");
        const el = document.querySelector("#insights-list");

        if (el instanceof HTMLElement) {
            el.dispatchEvent(event);
        }
    };

    onActionSelect = (action: mt.IInsightAction, insight: mt.IEventDataEx) => {
        if (action.code === INSIGHT_ACTIONS.print_tool_transfer) {
            const order_external_id = ReportLogic.getOrderExternalIdFromInsight(insight);
            const { process_num } = insight.extra_data;
            if (!order_external_id || !process_num) {
                return;
            }

            exportToolTransfer(order_external_id, process_num);
        }
    };

    onOrderChange = (order: IOrderModelBase) => {
        const comment = order.order_comment;
        onSaveComment(order.uuid, comment, this.props.reduxDispatch);
    };

    renderGantt() {
        const report_data = this.props.report.report_data;
        if (!report_data) {
            return <Loader message={translate("common.preparing_gantt_chart")} />;
        }

        return (
            <GanttPanes
                num_unscheduled_lines={this.props.properties.num_unscheduled_lines}
                count_non_empty_lines={this.props.properties.count_non_empty_lines}
                num_stock_requirements={this.props.properties.num_stock_requirements}
                gantt0={
                    <StockRequirementsGanttChart
                        key={"stock-requirements-" + this.props.show_stock_requirements.toString()}
                        source_type={GanttChartSource.stock_requirements}
                        line_group_uuid={this.props.line_group_uuid}
                    />
                }
                gantt1={
                    <GanttChart
                        key={"planning-table1-" + this.props.show_stock_requirements.toString()}
                        line_group_uuid={this.props.line_group_uuid}
                        source_type={GanttChartSource.planning_table_parallel}
                    />
                }
                gantt2={
                    this.props.show_unscheduled_orders && (
                        <UnscheduledGanttChart
                            key={"unscheduled-planning-table-" + this.props.show_stock_requirements.toString()}
                            line_group_uuid={this.props.line_group_uuid}
                            show_planning_details_filter={this.props.show_planning_details_filter}
                            source_type={GanttChartSource.gantt_unscheduled}
                            show_only_unscheduled={true}
                        />
                    )
                }
            />
        );
    }

    renderToolChanges = () => {
        const { report } = this.props;
        const { report_data } = report;
        if (!report || !report_data) {
            return <Loader />;
        }

        const { result } = report_data;
        const insights = (this.props.insights || []).filter(insight => insight.type === INSIGHT_TYPES.tool_setup);
        return (
            <>
                <div className="d-flex justify-content-end m-2 tool-changes-toolbar">
                    <button className="btn btn-primary" onClick={this.onDownloadHTML}>
                        <span>{translate("ShiftDetails.export_for_print", "Export for print")}</span>
                    </button>
                </div>

                <InsightsList
                    history={this.props.history}
                    insights={insights}
                    reportid={report.current_report_uuid || report.original_report_uuid || ""}
                    week={result.from_shift_time.week}
                    year={result.from_shift_time.year}
                    show_filter={false}
                    show_only_future={false}
                    show_links={false}
                    show_actions={false}
                    reverse_order={false}
                    allowed_actions={[INSIGHT_ACTIONS.print_tool_transfer]}
                    onActionSelect={this.onActionSelect}
                />
            </>
        );
    };

    renderMaterialForecast = () => {
        const { report, stock_report_uuid } = this.props;
        const report_uuid =
            this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.material_recommend_forecast_in
                ? stock_report_uuid
                : report.current_report_uuid || report.original_report_uuid;

        return (
            <MaterialStockForecastContainer
                report_uuid={report_uuid ?? null}
                line_group_uuid={this.props.line_group_uuid}
                planning_weeks={this.props.planning_weeks}
                selected_display_type={this.props.selected_display_type}
            />
        );
    };

    render() {
        const is_gantt_loading = this.props.report_loading || this.props.lines_orders_loading;
        const material_forecast_display_types: (keyof typeof PLANNING_TABLE_DISPLAY_TYPES)[] = [
            PLANNING_TABLE_DISPLAY_TYPES.material_forecast_out,
            PLANNING_TABLE_DISPLAY_TYPES.material_forecast_in,
            PLANNING_TABLE_DISPLAY_TYPES.material_forecast_wip,
            PLANNING_TABLE_DISPLAY_TYPES.material_recommend_forecast_in,
        ];
        const report_data: IReportModelEx | null = this.props.report.report_data;
        return (
            <section
                id="planning-table2"
                className={classNames(`planning-table`, {
                    "planning-table--compact": this.props.properties.waterfall_mode,
                })}
            >
                <article className="article" style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}>
                    <PlanningTableEditModal />
                    <OrderAdvancedMenu />
                    <PlanningDetails
                        show_planning_details_filter={this.props.show_planning_details_filter}
                        show_unscheduled_orders={this.props.show_unscheduled_orders}
                        line_group_uuid={this.props.line_group_uuid}
                        plant_uuid={this.props.plant_uuid}
                        position={this.props.planning_details_position}
                        size_ratio={this.getTableSizeRatio()}
                    />

                    {this.props.show_planning_details_filter && (
                        <ResizableHandle
                            direction={this.props.planning_details_position === "left" ? "vertical" : "horizontal"}
                            position={`${this.getResizableHandlePosition() * 100}%`}
                            onMoveStop={this.handleResizeStop}
                            onMove={this.handleResize}
                        />
                    )}

                    <Controls
                        show_planning_details_filter={this.props.show_planning_details_filter}
                        selected_display_type={this.props.selected_display_type}
                        planning_details_position={this.props.planning_details_position}
                        planning_details_size_ratio={this.getTableSizeRatio()}
                    >
                        <div className="display-chart-toolbar" style={{ height: 46 }}>
                            <div>
                                <DisplayTypesMenu
                                    selected_display_type={this.props.selected_display_type}
                                    line_group_uuid={this.props.line_group_uuid}
                                    plant_uuid={this.props.plant_uuid}
                                    onDisplayTypeChanged={this.props.onDisplayTypeChanged}
                                />
                            </div>
                            {this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.gantt && (
                                <>
                                    <div className="d-flex flex-grow-1 justify-content-end">
                                        <ZoomToolbar />
                                    </div>
                                    <LineStatusesPoller />

                                    <div className="gantt-chart-toolbar">
                                        {this.showWaterfallButton() && (
                                            <div className="react-toggle-wrapper react-toggle-wrapper--waterfall">
                                                <div className="form-group">
                                                    <label className="label-wrapper">
                                                        <Toggle
                                                            checked={this.props.properties.waterfall_mode}
                                                            onChange={this.handleToggleWaterfallMode}
                                                        />
                                                        {translate("Manufacturing.Planning.waterfall", "Waterfall")}
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                        <InsightTypesSelect hide_insight_types={[INSIGHT_TYPES.man_downtime]} />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="chart-content h-100 flex-grow-1 overflow-auto">
                            {this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.kpis && (
                                <div>
                                    <KpisContainer
                                        line_group_uuid={this.props.line_group_uuid}
                                        is_loading={is_gantt_loading}
                                    />
                                </div>
                            )}
                            {this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.gantt &&
                                this.renderGantt()}
                            {this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.tool_changes &&
                                this.renderToolChanges()}
                            {material_forecast_display_types.includes(this.props.selected_display_type) &&
                                this.renderMaterialForecast()}
                        </div>
                    </Controls>
                    <ShowGanttOrderDataOnClick
                        onOrderChange={this.onOrderChange}
                        report={report_data && report_data.result}
                    />
                </article>
            </section>
        );
    }
}

const mapState = (state: ReduxState) => {
    const report: ReportState = state.gantt_chart_report;
    const planning_table = state.gantt_chart_planning_table;
    const lines_orders: LineOrders[] = state.gantt_chart_lines_orders.lines_orders;
    const loading: boolean = state.gantt_chart_lines_orders.loading;
    const properties = state.gantt_chart_properties;
    const controls = state.gantt_chart_controls;
    const insights = state.gantt_chart_insights.insights;
    const show_stock_requirements = state.gantt_chart_planning_table.show_stock_requirements;
    const valid_lines_map = state.gantt_chart_planning_table.show_valid_lines.valid_lines_map || new Map();
    const recommend_plans = state.gantt_chart_recommend_plans.recommend_plans;

    const stock_report_uuid =
        recommend_plans && recommend_plans.length > 0
            ? recommend_plans[recommend_plans.length - 1].stock_report_uuid
            : undefined;
    const planning_weeks = properties.days > 0 ? Math.ceil(properties.days / 7) : 1;

    return {
        report,
        stock_report_uuid: stock_report_uuid !== "" ? stock_report_uuid : undefined,
        report_loading: report.report_loading,
        lines_orders_loading: loading,
        lines_orders,
        show_unscheduled_orders: planning_table.show_unscheduled_orders,
        unscheduled_orders: state.gantt_chart_lines_orders.unscheduled_orders,
        valid_lines: planning_table.show_valid_lines.valid_lines,
        error: planning_table.error,
        planning_weeks,
        properties,
        zoom_enabled: controls.zoom_enabled,
        insights,
        show_stock_requirements,
        single_line_mode: selectSingleLineMode(state),
        valid_lines_map,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(PlanningTable);
